import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKCheckbox,
  GKRadio,
  GKSearchSelect,
  GKSelect,
  GKTextField,
  Grid2,
  MenuItem,
  PhoneInput,
  RadioGroup,
  Show,
  SimpleLoader,
  Switch,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { SUBSCRIPTION_FREQUENCY } from "Pages/Revenue/utils";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import {
  AGREEMENT_SIGNATURE_SOURCE,
  KYC_OPTIONS,
  ONBOARDING_DATE_SELECTION,
} from "utils/constants";
import { PAYMENT_METHOD, refetchQuery } from "utils/helpers";
import { emailRegex } from "utils/validationSchema";

export interface ManagementFee {
  id: number;
  feeType: string;
  feeMethod: any;
  fee: number;
  calculationMethod: string;
  calculationBenchmark: string;
  calculationFrequency: any;
  billFrequency: string;
  minimumFee: number;
  maximumFee: number;
  outOfPocketExpenses: number;
  managementFeeSlab: any[];
  hsnCode: string;
  productName: string;
  minimumInvestment: number;
  availableFrequency: any[];
  includeDividend: boolean;
}

interface Props {
  type: string;
  customAgreementTemplate: {
    id: number;
    isFeePlan: boolean;
    isModelPortfolio: boolean;
    title: string;
    content: string;
    createdAt: string;
    company: number;
    value?: string;
    name?: string;
  }[];
  feePlanData: {
    id: number;
    name: string;
    status: boolean;
    revenueProducts: string[];
    company: number;
    createdBy: number;
    totalAumMapped: number;
    totalPortfolio: number;
    totalFeeGenerated: number;
    totalFeeCollected: any;
    managementFee: ManagementFee;
    performanceFee: any;
    description: string;
    subscriptionFee: any;
    advanceDeposit: any;
    bestValue: number;
  }[];
  estampTags: {
    id: number;
    tags: string;
    description: any;
    createdAt: string;
    updatedAt: string;
    company: number;
  }[];
}

interface OnboardflowInterface {
  id?: number;
  flowType: string;
  kycIdentificationType: string;
  onboardingDate: string;
  kycType: string;
  agreementType: string;
  defaultClientSignType: string;
  defaultAdvisorSignType: string;
  twoWaySign: boolean;
  name: string;
  estampDescription: string;
  estampTag: string;
  frequency: string;
  countryCode: string;
  phoneNumber: string;
  email: string;
  paymentMethods: string;
  company: number;
  feePlan: number;
  agreementTemplate: string;
  revenueWithGst: boolean;
  customizeSetting: {
    kyc: boolean;
    proofs: boolean;
    payment: boolean;
    agreement: boolean;
    riskprofiling: boolean;
    basketFeePlan: boolean;
    investmentDetails: boolean;
  };
}

const SubscriptionFlow = (props: Props) => {
  const { type, customAgreementTemplate, feePlanData, estampTags } = props;

  const userData = useAppSelector((state) => state?.user?.userData);

  const [hasError, setHasError] = useState(false);

  const form = useForm<OnboardflowInterface>({
    defaultValues: {
      flowType: type,
      kycIdentificationType: "only_kyc",
      kycType: "",
      agreementTemplate: "",
      agreementType: "standard",
      defaultAdvisorSignType: "",
      twoWaySign: false,
      name: "",
      countryCode: "+91",
      phoneNumber: "",
      defaultClientSignType: "",
      estampTag: "",
      estampDescription: "",
      email: "",
      frequency: "",
      onboardingDate: "",
      feePlan: null,
      paymentMethods: "",
      revenueWithGst: false,
      customizeSetting: {
        kyc: true,
        proofs: true,
        payment: true,
        agreement: true,
        riskprofiling: true,
        basketFeePlan: true,
        investmentDetails: true,
      },
    },
  });

  const { isFetching } = useFetchQuery({
    key: ["ON_BOARDING_FLOW", type],
    route: "/onboardflow/",
    requestBody: { flow_type: type },
    success: (response: any) => {
      form.reset({ ...response?.data?.data, flowType: type });
    },
  });

  const twoWaySignWatch = form.watch("twoWaySign");

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: OnboardflowInterface) =>
      axiosInstance.patch(`/onboardflow/${userData?.companyId}/`, data),
    onSuccess: () => {
      refetchQuery("advisor_initiated_advisory_flow");
      refetchQuery("client_initiated_advisory_flow");
    },
  });

  if (isFetching) return <SimpleLoader />;

  return (
    <form
      id="subscription-flow-form"
      onSubmit={form.handleSubmit((values) => mutate(values))}
    >
      <Grid2 container spacing={2}>
        {/* <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Onboarding Type
          </Typography>
        </Grid2> */}

        {/* <Grid2 container size={9} spacing={2}>
          <Grid2 size={12} mt={0.5}>
            <Typography
              fontSize={14}
              fontWeight={0}
              letterSpacing={"0.36px"}
              color="var(--neutral-900)"
            >
              How do you want to onboard clients?
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <RadioGroup
            // value={onboardingType}
            // onChange={(e) => setOnboardingType(e.target.value)}
            >
              <FormControlLabel
                value="portfolio_wise"
                control={<GKRadio />}
                label={
                  <Typography fontSize={16} fontWeight={400}>
                    Portfolio-wise
                  </Typography>
                }
              />
              <Grid2 ml={"34px"} mb={1}>
                <Typography fontSize={12} color={"var(--neutral-500)"}>
                  Onboard each portfolio separately (linked to different
                  baskets).
                </Typography>
              </Grid2>

              <FormControlLabel
                value="client_wise"
                control={<GKRadio />}
                label={
                  <Typography fontSize={16} fontWeight={400}>
                    Client-wise
                  </Typography>
                }
              />
              <Grid2 ml={"34px"}>
                <Typography fontSize={12} color={"var(--neutral-500)"}>
                  Onboard the client once, across all portfolios.
                </Typography>
              </Grid2>
            </RadioGroup>
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2> */}

        {/* <Grid2 size={9}>
          <Controller
            name="onboardingDate"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select client onboarding date",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                inputLabel="Which date do you prefer to show onboarding date?"
                disableClearable={false}
                options={ONBOARDING_DATE_SELECTION}
                value={
                  ONBOARDING_DATE_SELECTION?.find(
                    (dataItem) => dataItem.value === field.value,
                  )?.name || ""
                }
                onChange={(event, val) => field.onChange(val ? val?.value : "")}
                getOptionLabel={(option) => option.name || option}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2> */}
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            KYC
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.kyc"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include KYC in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>
          <Show.When isTrue={form.watch("customizeSetting.kyc")}>
            <Grid2 size={12}>
              <Controller
                name="kycType"
                control={form.control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel sx={{ color: "var(--black)" }}>
                      Mode Of KYC
                    </FormLabel>
                    <RadioGroup
                      {...field}
                      row
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <FormControlLabel
                        label="Only KRA"
                        value="only_kra"
                        checked={field.value === "only_kra"}
                        control={<GKRadio disableLeftPadding />}
                      />
                      {/* <FormControlLabel
                        label="If KRA not found then KYC"
                        value="if_kra_not_found_then_kyc"
                        checked={field.value === "if_kra_not_found_then_kyc"}
                        control={<GKRadio />}
                      /> */}
                      <FormControlLabel
                        label="KRA+KYC"
                        value="kra_and_kyc"
                        checked={field.value === "kra_and_kyc"}
                        control={<GKRadio />}
                      />
                      <FormControlLabel
                        label="Only KYC"
                        value="only_kyc"
                        checked={field.value === "only_kyc"}
                        control={<GKRadio />}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="kycIdentificationType"
                control={form.control}
                render={({ field }) => (
                  <GKSelect {...field} inputLabel="KYC Type">
                    <MenuItem style={{ display: "none" }} value="">
                      Select Identification Type
                    </MenuItem>
                    {KYC_OPTIONS?.map((data) => (
                      <MenuItem value={data?.value} key={data?.value}>
                        {data?.name}
                      </MenuItem>
                    ))}
                  </GKSelect>
                )}
              />
            </Grid2>
          </Show.When>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Agreement
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.agreement"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include agreement in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>
          <Show.When isTrue={form.watch("customizeSetting.agreement")}>
            <Grid2 size={4}>
              <Controller
                name="agreementTemplate"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    inputLabel="Agreement Template"
                    disableClearable={false}
                    options={customAgreementTemplate}
                    value={
                      customAgreementTemplate?.find(
                        (dataItem) => dataItem.value === field.value,
                      )?.name || ""
                    }
                    onChange={(event, val) =>
                      field.onChange(val ? val?.value : "")
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={8}>
              <Typography>Agreement Type</Typography>
              <Box sx={{ display: "flex" }}>
                <Controller
                  name="agreementType"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <RadioGroup
                        {...field}
                        row
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <FormControlLabel
                          label="Standard"
                          value="standard"
                          control={<GKRadio disableLeftPadding />}
                        />
                        <FormControlLabel
                          label="Stamp"
                          value="stamp"
                          control={<GKRadio />}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Box>
            </Grid2>
            <Show.When isTrue={form.watch("agreementType") === "stamp"}>
              <Grid2 size={12}>
                <Controller
                  name="estampTag"
                  control={form.control}
                  rules={{
                    required: {
                      value: form.watch("agreementType") === "stamp",
                      message: "Please select an estamp",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKSearchSelect
                      {...field}
                      requiredField
                      inputLabel="Select Estamp"
                      disableClearable={false}
                      options={estampTags?.map((d: any) => {
                        return {
                          ...d,
                          label: d.tags,
                          value: d.id,
                        };
                      })}
                      value={
                        estampTags?.find(
                          (dataItem: any) => dataItem.id === field.value,
                        )?.tags || ""
                      }
                      onChange={(event, val) =>
                        field.onChange(val ? val?.value : "")
                      }
                      getOptionLabel={(option) => option.label || option}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
            </Show.When>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                control={form.control}
                name="twoWaySign"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                      />
                    }
                    labelPlacement="end"
                    label={
                      <Typography fontSize={16} color="#121926">
                        2-way signing (Client & Advisor)
                      </Typography>
                    }
                  />
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="defaultClientSignType"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select client sign type",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    requiredField
                    inputLabel="Default Client sign type"
                    disableClearable={false}
                    options={AGREEMENT_SIGNATURE_SOURCE?.filter(
                      (d) => d.value === "aadhaar",
                    )}
                    value={
                      AGREEMENT_SIGNATURE_SOURCE?.find(
                        (dataItem) => dataItem.value === field.value,
                      )?.name || ""
                    }
                    onChange={(event, val) =>
                      field.onChange(val ? val?.value : "")
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Show.When isTrue={twoWaySignWatch}>
              <Grid2 size={6}>
                <Controller
                  name="defaultAdvisorSignType"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please select advisor sign type",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKSearchSelect
                      {...field}
                      requiredField
                      inputLabel="Default Advisor sign type"
                      disableClearable={false}
                      options={AGREEMENT_SIGNATURE_SOURCE?.filter(
                        (d) => d.value === "aadhaar",
                      )}
                      value={
                        AGREEMENT_SIGNATURE_SOURCE?.find(
                          (dataItem) => dataItem.value === field.value,
                        )?.name || ""
                      }
                      onChange={(event, val) =>
                        field.onChange(val ? val?.value : "")
                      }
                      getOptionLabel={(option) => option.name || option}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
            </Show.When>

            <Grid2 size={12}>
              <Typography color="#697586">
                Advisor Communication Details
              </Typography>
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name="name"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKTextField
                    {...field}
                    inputLabel="Name"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="phoneNumber"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <PhoneInput
                    {...field}
                    inputLabel="Phone Number"
                    onSelectChange={(val: any) => {
                      form.setValue("countryCode", val);
                    }}
                    onTextChange={(event: any) => {
                      field.onChange(event);
                    }}
                    onTextValue={field.value}
                    onSelectValue={form.getValues("countryCode")}
                    hasError={hasError}
                    setHasError={setHasError}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={4}>
              <Controller
                name="email"
                control={form.control}
                rules={{
                  pattern: {
                    value: emailRegex,
                    message: "Please enter a valid email",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKTextField
                    {...field}
                    inputLabel="Email"
                    error={!!error}
                    helperText={!!error && error?.message}
                  />
                )}
              />
            </Grid2>
          </Show.When>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Onboarding Fees & Basket
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.basketFeePlan"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      checked={field.value}
                      onChange={(event) => {
                        if (!event.target.checked) {
                          form.setValue("customizeSetting.payment", false);
                        }
                        field.onChange(event.target.checked);
                      }}
                    />
                  }
                  label={
                    "Include basket selection and applicable fees in the onboarding flow."
                  }
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>

          <Show.When isTrue={form.watch("customizeSetting.basketFeePlan")}>
            <Grid2 size={6}>
              <Controller
                name="feePlan"
                control={form.control}
                render={({ field }) => {
                  return (
                    <GKSearchSelect
                      {...field}
                      inputLabel="Advance Fee Plan"
                      onChange={(e, val) => {
                        field.onChange(val ? val?.value : "");
                      }}
                      value={
                        feePlanData?.find(
                          (dataItem: any) => dataItem.value === field.value,
                        )?.name
                      }
                      options={
                        feePlanData?.map((d: any) => {
                          return {
                            label: d.name,
                            value: d.id,
                            ...d,
                          };
                        }) || []
                      }
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="frequency"
                control={form.control}
                render={({ field }) => {
                  return (
                    <GKSearchSelect
                      {...field}
                      inputLabel="Fee Plan Frequency"
                      options={SUBSCRIPTION_FREQUENCY}
                      value={
                        SUBSCRIPTION_FREQUENCY?.find(
                          (dataItem) => dataItem.value === field.value,
                        )?.name
                      }
                      onChange={(e, val) => {
                        field.onChange(val ? val?.value : "");
                      }}
                    />
                  );
                }}
              />
            </Grid2>
          </Show.When>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Payment
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.payment"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include payment in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>
          <Show.When isTrue={form.watch("customizeSetting.payment")}>
            <Grid2 size={6}>
              <Controller
                name="paymentMethods"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select payment collection method",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    requiredField
                    inputLabel="Payment Collection Method"
                    disableClearable={false}
                    options={PAYMENT_METHOD}
                    value={
                      PAYMENT_METHOD?.find(
                        (dataItem) => dataItem.value === field.value,
                      )?.name || ""
                    }
                    onChange={(event, val) => {
                      field.onChange(val ? val?.value : "");
                    }}
                    getOptionLabel={(option) => option.name || option}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name="revenueWithGst"
                control={form.control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel>Revenue with GST</FormLabel>
                    <RadioGroup
                      {...field}
                      row
                      onChange={(e) =>
                        field.onChange(e.target.value === "true")
                      }
                    >
                      <FormControlLabel
                        label="Yes"
                        value="true"
                        control={<GKRadio disableLeftPadding />}
                      />
                      <FormControlLabel
                        label="No"
                        value="false"
                        control={<GKRadio />}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid2>
          </Show.When>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Additional Settings
          </Typography>
        </Grid2>
        <Grid2 container size={9} spacing={2}>
          <Grid2 size={9}>
            <Controller
              name="onboardingDate"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select client onboarding date",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Which date do you prefer to show onboarding date?"
                  disableClearable={false}
                  options={ONBOARDING_DATE_SELECTION}
                  value={
                    ONBOARDING_DATE_SELECTION?.find(
                      (dataItem) => dataItem.value === field.value,
                    )?.name || ""
                  }
                  onChange={(event, val) =>
                    field.onChange(val ? val?.value : "")
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.investmentDetails"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Investment source of funds"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>

          <Show.When isTrue={type === "client_initiated_advisory_flow"}>
            <Grid2 size={12}>
              <Controller
                name="customizeSetting.proofs"
                control={form.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <GKCheckbox
                        checked={field.value}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                      />
                    }
                    label={"Include proofs in onboarding flow"}
                    labelPlacement="end"
                  />
                )}
              />
            </Grid2>
          </Show.When>
          <Grid2 size={12}>
            <Controller
              name="customizeSetting.riskprofiling"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <GKCheckbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={"Include risk profiling in onboarding flow"}
                  labelPlacement="end"
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} textAlign={"right"}>
          <GKButton
            form="subscription-flow-form"
            variant="contained"
            type="submit"
            loading={isLoading}
            disabled={hasError}
          >
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default SubscriptionFlow;
